/**
 * StatusBadge Component
 * Reusable status badge with consistent styling across the application
 *
 * Usage:
 * <StatusBadge status="active" />
 * <StatusBadge status="pending" variant="solid" />
 * <StatusBadge status="sold" size="md" />
 */

import { getStatusColor, getPropertyBadgeColor } from '@/constants/styles';

export interface StatusBadgeProps {
    /** Status string (e.g., 'active', 'pending', 'sold') */
    status: string;
    /** Badge variant: 'light' for pastel colors, 'solid' for high contrast */
    variant?: 'light' | 'solid';
    /** Badge size */
    size?: 'sm' | 'md';
    /** Additional CSS classes */
    className?: string;
    /** Custom label (defaults to capitalized status) */
    label?: string;
}

const SIZE_CLASSES = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
} as const;

/**
 * Capitalize first letter of each word
 */
function formatStatusLabel(status: string): string {
    return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

export function StatusBadge({
    status,
    variant = 'light',
    size = 'sm',
    className = '',
    label,
}: StatusBadgeProps) {
    const colorClass = variant === 'solid'
        ? getPropertyBadgeColor(status)
        : getStatusColor(status);

    const sizeClass = SIZE_CLASSES[size];
    const displayLabel = label ?? formatStatusLabel(status);

    return (
        <span
            className={`inline-flex items-center font-medium rounded-full ${sizeClass} ${colorClass} ${className}`.trim()}
        >
            {displayLabel}
        </span>
    );
}

export default StatusBadge;
